import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { getPaymentConfirmationViewModel } from '@app/payments/selectors/payments.selector';
import { Store } from '@ngrx/store';
import { PanAppState } from '@panamax/app-state';
import { FileHandlerPaymentsService } from '@payments/services/file-handler-payments.service';
import { PATHS } from '@shared/constants/paths';
import { FEATURES } from '@shared/constants/splitio-features';
import { PaymentStoreService } from '@usf/ngrx-payment';
import { CustomerReview, PaymentSummary } from '@usf/payment-types';
import { Observable, combineLatest, filter, map } from 'rxjs';
import { PaymentConfirmationPageVM } from '../payment-confirmation.page';

export interface PaymentData {
  paymentSummary: PaymentSummary;
  customers: CustomerReview[];
}

@Injectable({
  providedIn: 'root',
})
export class PaymentConfirmationService {
  constructor(
    private store: Store,
    private fileHandlerService: FileHandlerPaymentsService,
    private paymentStoreService: PaymentStoreService,
    private router: Router,
    private panAppState: PanAppState,
  ) {}

  getPayment$(): Observable<PaymentConfirmationPageVM> {
    return combineLatest([
      this.store.select(getPaymentConfirmationViewModel),
      this.panAppState.feature$([
        FEATURES.split_user_payments_share_payment_confirmation.name,
      ]),
      this.panAppState.feature$([
        FEATURES.split_dynamic_payments_order_number.name,
      ]),
    ]).pipe(
      map(([vm, shareFlag, orderNumberFlag]) => {
        return {
          ...vm,
          shareFlag,
          showOrderNumber: orderNumberFlag,
        };
      }),
    );
  }

  clearPayments() {
    this.paymentStoreService.clearPayments();
  }

  sharePaymentConfirmation(paymentData: PaymentData) {
    this.fileHandlerService.sharePaymentConfirmation(paymentData);
  }

  printPaymentConfirmation(paymentData: PaymentData) {
    this.fileHandlerService.printPaymentConfirmation(paymentData);
  }

  downloadPaymentConfirmation(paymentData: PaymentData) {
    this.fileHandlerService.downloadPaymentConfirmation(paymentData);
  }

  canActivate(): Observable<boolean> {
    return combineLatest([this.getPayment$(), this.panAppState.user$]).pipe(
      filter(([, user]) => !!user),
      map(([vm]) => {
        if (!vm.selectedCustomersAmount) {
          this.router.navigate([PATHS.PAYMENTS]);
          return false;
        }

        if (!vm.overallCustomerBankAccounts.length) {
          this.router.navigate([PATHS.PAYMENTS_REVIEW_PAYMENT]);
          return false;
        }

        return true;
      }),
    );
  }
}
